import { Box, Stack, Typography, Button, Chip } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import {
  ExclamationCircleOutlined,
  CloseOutlined,
  FormOutlined,
  DatabaseOutlined,
  SettingOutlined,
  CheckCircleFilled,
  ReloadOutlined,
  FileAddOutlined,
} from '@ant-design/icons';
import { NoReportState } from '../types';

interface Props {
  applicationId: string;
  applicationNumber?: number;
  ownerIndex: number;
  ownerName: string;
  state: NoReportState;
  missingFields?: string[];
  resolved?: boolean;
  onRetry: () => void;
  onDismiss: () => void;
}

interface Palette {
  accent: string;
  headerBg: string;
  borderSoft: string;
  iconBg: string;
}

const paletteFor = (state: NoReportState, theme: ReturnType<typeof useTheme>): Palette => {
  if (state === 'gated_pii') {
    return {
      accent: theme.palette.warning.dark,
      headerBg: alpha(theme.palette.warning.dark, 0.07),
      borderSoft: alpha(theme.palette.warning.dark, 0.22),
      iconBg: alpha(theme.palette.warning.dark, 0.15),
    };
  }
  if (state === 'ready') {
    return {
      accent: theme.palette.warning.main,
      headerBg: alpha(theme.palette.warning.main, 0.06),
      borderSoft: alpha(theme.palette.warning.main, 0.25),
      iconBg: alpha(theme.palette.warning.main, 0.15),
    };
  }
  return {
    accent: theme.palette.text.secondary,
    headerBg: alpha(theme.palette.text.secondary, 0.05),
    borderSoft: alpha(theme.palette.text.secondary, 0.2),
    iconBg: alpha(theme.palette.text.secondary, 0.12),
  };
};

const headline = (state: NoReportState): string => {
  if (state === 'ready') return 'Credit report not available yet';
  if (state === 'gated_pii') return 'Missing data for credit pull';
  return 'No credit-report integration';
};

const description = (state: NoReportState, ownerName: string, appLabel: string): string => {
  if (state === 'ready') {
    return `${ownerName} has no credit report on file. Generate one on ${appLabel} — Credit Report tab, then retry this analysis.`;
  }
  if (state === 'gated_pii') {
    return `${ownerName}'s profile is missing fields required by credit bureaus. Complete them on ${appLabel} — Owners tab, generate the report, then retry.`;
  }
  return `This tenant has no active credit-report integration. An admin needs to enable one in Settings — External Services before credit pulls are possible.`;
};

const NoReportCard = ({
  applicationId,
  applicationNumber,
  ownerName,
  state,
  missingFields,
  resolved,
  onRetry,
  onDismiss,
}: Props) => {
  const theme = useTheme();
  const p = paletteFor(state, theme);
  const openAppUrl = `/applications/${applicationId}`;
  const appLabel = applicationNumber !== undefined ? `Application #${applicationNumber}` : 'the application';
  const primaryDestination: { href: string; label: string } =
    state === 'no_integrations'
      ? { href: '/settings/external-services', label: 'Open External Services' }
      : state === 'gated_pii'
        ? { href: `${openAppUrl}?tab=owners`, label: `Open ${appLabel} → Owners` }
        : { href: `${openAppUrl}?tab=credit-report`, label: `Open ${appLabel} → Credit Report` };

  return (
    <Box
      sx={{
        borderRadius: 2,
        p: 1.75,
        mb: 2,
        bgcolor: p.headerBg,
        border: `1px solid ${p.borderSoft}`,
        borderLeft: `3px solid ${p.accent}`,
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box sx={{
          width: 32, height: 32, borderRadius: 1.25, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: p.iconBg,
        }}>
          {state === 'no_integrations' ? (
            <DatabaseOutlined style={{ fontSize: 16, color: p.accent }} />
          ) : state === 'ready' ? (
            <FileAddOutlined style={{ fontSize: 16, color: p.accent }} />
          ) : (
            <ExclamationCircleOutlined style={{ fontSize: 17, color: p.accent }} />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontWeight: 700, fontSize: '0.9375rem',
            color: theme.palette.text.primary, mb: 0.5, lineHeight: 1.35,
          }}>
            {headline(state)}
          </Typography>
          <Typography sx={{
            fontSize: '0.875rem',
            color: theme.palette.text.secondary,
            lineHeight: 1.55,
          }}>
            {description(state, ownerName, appLabel)}
          </Typography>

          {state === 'gated_pii' && missingFields && missingFields.length > 0 && (
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
              {missingFields.map((field) => (
                <Chip
                  key={field}
                  label={field}
                  size="small"
                  icon={<ExclamationCircleOutlined style={{ fontSize: 10 }} />}
                  sx={{
                    height: 22,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    bgcolor: alpha(p.accent, 0.12),
                    color: p.accent,
                    border: `1px solid ${alpha(p.accent, 0.3)}`,
                    '& .MuiChip-icon': { color: p.accent, ml: 0.5 },
                    '& .MuiChip-label': { px: 0.875 },
                  }}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Stack>

      {resolved ? (
        <Chip
          icon={<CheckCircleFilled style={{ fontSize: 11 }} />}
          label="Action taken"
          size="small"
          sx={{
            mt: 1.5,
            height: 22,
            fontSize: '0.75rem',
            fontWeight: 600,
            bgcolor: alpha(theme.palette.text.primary, 0.08),
            color: theme.palette.text.disabled,
            '& .MuiChip-icon': { color: theme.palette.text.disabled },
            '& .MuiChip-label': { px: 1 },
          }}
        />
      ) : (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.75 }}>
          <Button
            component={RouterLink as any}
            to={primaryDestination.href}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="contained"
            startIcon={
              state === 'no_integrations'
                ? <SettingOutlined style={{ fontSize: 13 }} />
                : <FormOutlined style={{ fontSize: 13 }} />
            }
            sx={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              py: 0.625,
              px: 1.5,
              textTransform: 'none',
              bgcolor: p.accent,
              color: '#fff',
              '&:hover': { bgcolor: p.accent, filter: 'brightness(0.92)' },
            }}
          >
            {primaryDestination.label}
          </Button>
          {state !== 'no_integrations' ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ReloadOutlined style={{ fontSize: 13 }} />}
              onClick={onRetry}
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 600,
                py: 0.625,
                px: 1.5,
                textTransform: 'none',
                borderColor: alpha(p.accent, 0.5),
                color: p.accent,
                '&:hover': {
                  borderColor: p.accent,
                  bgcolor: alpha(p.accent, 0.06),
                },
              }}
            >
              Retry analysis
            </Button>
          ) : (
            <Button
              size="small"
              startIcon={<CloseOutlined style={{ fontSize: 13 }} />}
              onClick={onDismiss}
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 600,
                py: 0.625,
                px: 1.5,
                textTransform: 'none',
                color: theme.palette.text.secondary,
              }}
            >
              Dismiss
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default NoReportCard;
